import type { ApiErrorBody } from '../types/api';

/**
 * A request the server understood and refused.
 *
 * Callers branch on `code`, never on the human-readable message: the message
 * is meant for a person, the code is the contract.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;
  readonly requestId: string;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details ?? {};
    this.requestId = body.request_id ?? '';
  }

  /** Field-level validation messages, when the server supplied any. */
  fieldErrors(): Record<string, string[]> {
    const result: Record<string, string[]> = {};
    for (const [field, value] of Object.entries(this.details)) {
      if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
        result[field] = value as string[];
      }
    }
    return result;
  }
}

/** The request never produced an answer: offline, DNS failure, timeout, abort. */
export class NetworkError extends Error {
  constructor(message = 'Сеть недоступна.', cause?: unknown) {
    // The underlying failure travels in the standard `cause` option rather
    // than a field of our own, so anything that already knows how to unwrap
    // an Error chain keeps working.
    super(message, cause === undefined ? undefined : { cause });
    this.name = 'NetworkError';
  }
}

/** The stored credentials are gone or rejected; the user has to sign in again. */
export class AuthenticationRequiredError extends Error {
  constructor(message = 'Сессия истекла. Войдите заново.') {
    super(message);
    this.name = 'AuthenticationRequiredError';
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

export function isNetworkError(value: unknown): value is NetworkError {
  return value instanceof NetworkError;
}

export function isAuthenticationRequired(value: unknown): value is AuthenticationRequiredError {
  return value instanceof AuthenticationRequiredError;
}
